import React, { useState, useEffect, useMemo } from "react";
import { useApi } from "../../../context/ApiProvider";
import Card from "../../Helpers/Card";
import ActivityIndicator from "../../Helpers/ActivityIndicator";
import { Line } from "react-chartjs-2";
import moment from "moment";

// ---------- Helpers ----------
function formatMonth(ymd) {
  const d = new Date(ymd);
  return d.toLocaleDateString("sv-SE", { month: "short", year: "numeric" });
}
function fmtInt(v) {
  return v == null ? "—" : v.toLocaleString("sv-SE");
}
function fmtPct(v) {
  return v == null ? "—" : `${Number(v).toFixed(2)} %`;
}

const SCOPE_OPTIONS = [
  { key: "not_only_pulse_total", label: "All (excl pulse)" },
  { key: "only_pulse_total", label: "Only pulse" },
  { key: "reflection", label: "Reflection" },
  { key: "development", label: "Development" },
  { key: "rolfing", label: "Rolfing" },
  { key: "actionplan", label: "Actionplan" },
  { key: "pulse", label: "Pulse" },
];

function UserActivity() {
  const [userActivity, setUserActivity] = useState([]); // API-svar
  const [selectedClients, setSelectedClients] = useState([]); // valda klient-id:n
  const [groupClients, setGroupClients] = useState(true); // true = aggregerat (client_id=0 eller en vald klient)
  const [clients, setClients] = useState(false); // klientlista för dropdown
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [dates, setDates] = useState({
    fromDate: moment().subtract(3, "months").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });
  const [fetchErrorMessage, setFetchErrorMessage] = useState(null);

  // ENDA filtret för vad som visas (serie/”modul”)
  const [scope, setScope] = useState("not_only_pulse_total");

  const Api = useApi();

  // Första laddningen: hämta klienter + data
  useEffect(() => {
    fetchClients();
    fetchUserActivity();
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // När valda klienter ändras
  useEffect(() => {
    if (!selectedClients.length) return;
    fetchUserActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClients]);

  // När ”groupClients” togglas: hämta data för aktuellt läge
  useEffect(() => {
    fetchUserActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupClients]);

  // Hämta activity
  function fetchUserActivity() {
    if (isFetching) return;
    setIsFetching(true);
    setFetchErrorMessage(null);

    // Bygg URL-parametrar: UNIX (sekunder) för from/to + groupClients-flaggan
    const dateUrlParams = `?fromDate=${moment(dates.fromDate)
      .startOf("day")
      .unix()}&toDate=${moment(dates.toDate).endOf("day").unix()}&groupClients=${groupClients ? 1 : 0
      }${selectedClients.length ? `&clientIds=${selectedClients.join(",")}` : ""}`;

    Api.get(`/rolf/useractivity/${dateUrlParams}`)
      .then((data) => {
        setUserActivity(data || []);
      })
      .catch((error) => {
        setFetchErrorMessage("Failed to fetch user activity");
        console.log(error);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }

  // Hämta klientlista
  function fetchClients() {
    Api.get("/rolf/clients")
      .then((data) => {
        setClients((data || []).filter((c) => c.active && c.paying));
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function handleClientSelectChange(event) {
    const values = Array.from(event.target.selectedOptions || []).map((option) => option.value).filter(Boolean);
    setSelectedClients(values);
  }
  function handleGroupClientsChange(event) {
    setGroupClients(event.target.checked);
  }

  // ----------------- Gemensamma derivat -----------------

  // Alla månader i datasetet (sorterade). Används både i enkelvy och multiklient-läge.
  const allMonths = useMemo(() => {
    const set = new Set(userActivity.map((r) => r.run_month));
    return Array.from(set).sort(); // 'YYYY-MM-DD' sorterar lexikografiskt rätt
  }, [userActivity]);

  // Index: month -> Map(module -> row) (för enkelvy: globalt eller vald klient)
  const byMonthSingle = useMemo(() => {
    const m = new Map();
    for (const r of userActivity) {
      if (!m.has(r.run_month)) m.set(r.run_month, new Map());
      m.get(r.run_month).set(r.module, r);
    }
    return m;
  }, [userActivity]);

  // Tabellrader för enkelvy (graf + tabell) – en rad per månad för chosen scope
  const tableRowsSingle = useMemo(() => {
    return allMonths.map((month) => {
      const row = byMonthSingle.get(month)?.get(scope);
      return {
        run_month: month,
        total_users: row?.total_users ?? null,
        active_7d: row?.active_7d ?? null,
        active_30d: row?.active_30d ?? null,
        ratio_7d: row?.ratio_7d ?? null,
        ratio_30d: row?.ratio_30d ?? null,
      };
    });
  }, [allMonths, byMonthSingle, scope]);

  // Dataset för grafen (endast i enkelvy)
  const lineDataSingle = useMemo(() => {
    return {
      labels: tableRowsSingle.map((r) => r.run_month),
      datasets: [
        {
          label: "Active users (%)",
          data: tableRowsSingle.map((r) =>
            r?.ratio_30d == null
              ? null
              : Math.round(Number(r.ratio_30d) * 100) / 100
          ),
          fill: false,
          borderColor: "#36ABF0",
          backgroundColor: "#36ABF0",
          spanGaps: true,
        },
      ],
    };
  }, [tableRowsSingle]);

  // ----------------- Multiklient-läge (groupClients === false och ingen vald klient) -----------------

  // Grupp: client_id -> rader
  const byClient = useMemo(() => {
    const m = new Map();
    for (const r of userActivity) {
      const cid = String(r.client_id);
      if (!m.has(cid)) m.set(cid, []);
      m.get(cid).push(r);
    }
    return m;
  }, [userActivity]);

  // Lookup: clientId -> client-objekt (för namn i dropdown/sortering)
  const clientById = useMemo(() => {
    const m = new Map();
    (clients || []).forEach((c) => m.set(String(c.ID), c));
    return m;
  }, [clients]);

  const resolveClientName = (clientId, rows = []) => {
    const fromApi = rows.find((row) => row?.clientName)?.clientName;
    if (fromApi) return fromApi;
    return clientById.get(String(clientId))?.name ?? `Client ${clientId}`;
  };

  // Pivottabell per klient:
  // { clientId: [ {month, total_users, ratio}, ... ] } för den valda serien (scope)
  const perClientDataset = useMemo(() => {
    const out = {};
    for (const [cid, rowsForClient] of byClient.entries()) {
      // indexera per månad -> modul → rad
      const monthMap = new Map();
      for (const r of rowsForClient) {
        if (!monthMap.has(r.run_month)) monthMap.set(r.run_month, new Map());
        monthMap.get(r.run_month).set(r.module, r);
      }
      // bygg en rad per månad för exakt en serie (scope)
      out[cid] = allMonths.map((month) => {
        const row = monthMap.get(month)?.get(scope);
        return {
          month,
          total_users: row?.total_users ?? null,
          ratio: row?.ratio_30d ?? null, // 0–100
          active_30d: row?.active_30d ?? null,
        };
      });
    }
    return out;
  }, [byClient, allMonths, scope]);

  const selectedClientLabel = useMemo(() => {
    if (!selectedClients.length) return null;
    return selectedClients
      .map((clientId) => clientById.get(String(clientId))?.name ?? `Client ${clientId}`)
      .join(", ");
  }, [clientById, selectedClients]);

  // ---------- Render ----------

  if (isLoading) {
    return (
      <span>
        <ActivityIndicator color="black" /> Loading...
      </span>
    );
  }

  const scopeSelectedStyle = {
    fontWeight: "bold",
    textDecoration: "underline",
    color: "inherit",
    cursor: "pointer",
  };
  const scopeBaseStyle = { cursor: "pointer" };

  // Multiklient-läge = visa EN pivottabell med rader = klienter, kolumner = månader.
  const showMultiClientTable = !groupClients && selectedClients.length === 0;

  return (
    <div className="grid-container">
      <section className="grow">
        <h4>User activity</h4>
        <p>
          Here you can see the share of active users compared to total users.
          Active users are users that have been active during the month. Select a
          client or view global statistics.
        </p>

        {selectedClientLabel ? (
          <p className="meta">Grouped client selection: {selectedClientLabel}</p>
        ) : null}

        {fetchErrorMessage && (
          <p style={{ color: "red" }}>{fetchErrorMessage}</p>
        )}

        {/* Scope-filter (en serie i taget) */}
        <p style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {SCOPE_OPTIONS.map((opt) => (
            <a
              key={opt.key}
              onClick={() => setScope(opt.key)}
              style={scope === opt.key ? scopeSelectedStyle : scopeBaseStyle}
            >
              {opt.label}
            </a>
          ))}
        </p>

        {/* Enkelvy: graf + tabell (gäller när groupClients=true ELLER en specifik klient är vald) */}
        {!showMultiClientTable && (
          <>
            {userActivity && (
              <Line
                data={lineDataSingle}
                options={{
                  scales: {
                    y: {
                      min: 0,
                      max: 100,
                      ticks: {
                        callback: (value) => value + "%",
                      },
                    },
                  },
                  cubicInterpolationMode: "monotone",
                }}
              />
            )}

            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th style={{ textAlign: "right" }}>Total users</th>
                  <th style={{ textAlign: "right" }}>Active (1m)</th>
                  <th style={{ textAlign: "right" }}>Ratio (1m)</th>
                </tr>
              </thead>
              <tbody>
                {tableRowsSingle.map((r) => (
                  <tr key={r.run_month}>
                    <td>{formatMonth(r.run_month)}</td>
                    <td style={{ textAlign: "right" }}>{fmtInt(r.total_users)}</td>
                    <td style={{ textAlign: "right" }}>{fmtInt(r.active_30d)}</td>
                    <td style={{ textAlign: "right" }}>{fmtPct(r.ratio_30d)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Multiklient-läge: Pivottabell (inga grafer) */}
        {showMultiClientTable && (
          <table>
            <thead>
              <tr>
                <th>Client</th>
                {allMonths.map((m) => (
                  <th key={m} style={{ textAlign: "right" }}>
                    {formatMonth(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(perClientDataset)
                // Sortera klienter efter namn (fallback ID)
                .sort(([aId], [bId]) => {
                  const a = resolveClientName(aId, perClientDataset[aId]).toString();
                  const b = resolveClientName(bId, perClientDataset[bId]).toString();
                  return a.localeCompare(b, "sv");
                })
                // Hoppa över klienter utan någon datapunkt för valt scope (alla celler null)
                .filter(([, arr]) =>
                  arr.some((x) => x.total_users != null || x.ratio != null)
                )
                .map(([cid, arr]) => (
                  <tr key={cid}>
                    <td>{resolveClientName(cid, byClient.get(String(cid)) || [])}</td>
                    {arr.map(({ month, total_users, ratio, active_30d }) => {
                      return (
                        <td key={`${cid}-${month}`} style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          {total_users == null && ratio == null ? ("—")
                            :
                            (
                              <>
                                <div >{fmtPct(ratio)}</div>
                                <div className="meta">{fmtInt(total_users)} / {fmtInt(active_30d)}</div>
                              </>
                            )}
                        </td>
                      )
                    }
                    )}

                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Sidokolumn: klient- och datumval */}
      <section>
        <h4>Select client</h4>
        <Card>
          <label htmlFor="selectClient">Select clients:</label>
          <select
            name="selectClient"
            id="selectClient"
            onChange={handleClientSelectChange}
            className="formControl"
            value={selectedClients}
            multiple
            size={Math.min((clients || []).length + 1, 12)}
          >
            {clients &&
              clients.map((client) => {
                return (
                  <option value={client.ID} key={client.ID}>
                    {client.name}
                  </option>
                );
              })}
          </select>

          <label className="checkbox">
            <input
              checked={groupClients}
              type="checkbox"
              name="groupClients"
              id="groupClients"
              onChange={handleGroupClientsChange}
            />
            <span>Group all clients</span>
          </label>
        </Card>

        <h4>Select time period</h4>
        <Card>
          <form>
            <label htmlFor="fromDate">From date:</label>
            <input
              type="date"
              name="fromDate"
              id="fromDate"
              value={dates.fromDate}
              onChange={(e) =>
                setDates({ ...dates, fromDate: e.target.value })
              }
              className="formControl"
            />
            <label htmlFor="toDate">To date:</label>
            <input
              type="date"
              name="toDate"
              id="toDate"
              value={dates.toDate}
              onChange={(e) => setDates({ ...dates, toDate: e.target.value })}
              className="formControl"
            />

            <button type="button" onClick={() => fetchUserActivity()}>
              {isFetching && <ActivityIndicator color="black" />} Select dates
            </button>
          </form>
        </Card>
      </section>
    </div>
  );
}

export default UserActivity;
